//similar application component which we implemented the feature

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  collection, onSnapshot, doc, updateDoc, deleteDoc, query,
  orderBy, where, getDocs, getDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase-config";
import {
  Table, TableHead, TableBody, TableRow, TableCell, Button, TextField,
  CircularProgress, TableSortLabel, TableContainer, Paper, useMediaQuery,
  Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Select,
  MenuItem, TablePagination, IconButton, Stack, Box, InputAdornment,
  alpha, Typography, Chip, Tooltip, FormControl, InputLabel, Card,
  CardContent, Grid, Collapse, Badge, Divider,
  styled
} from "@mui/material";
import {
  Eye, Trash2, Search, Filter, X, TrendingUp, Receipt,
  ShoppingCart, CreditCard, ArrowUpIcon, ArrowDownIcon
} from "lucide-react";
import InvoiceModal from "../components/invoice-modal";
import appTheme from "../theme";
import debounce from 'lodash.debounce';
import ReceiptIcon from '@mui/icons-material/Receipt';

// **STYLED COMPONENTS WITH LUXURY THEME**
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: appTheme.borderRadius.rounded,
  boxShadow: appTheme.shadows.soft,
  border: `1px solid ${appTheme.colors.borderSubtle}`,
  transition: appTheme.transitions.luxury,
  backgroundColor: appTheme.colors.surface,
  "&:hover": {
    boxShadow: appTheme.shadows.medium,
    transform: "translateY(-2px)",
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: appTheme.borderRadius.rounded,
  boxShadow: appTheme.shadows.medium,
  border: `1px solid ${appTheme.colors.borderPrimary}`,
  maxHeight: "calc(100vh - 300px)",
  backgroundColor: appTheme.colors.surface,
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: appTheme.gradients.hero,
  "& .MuiTableCell-head": {
    color: appTheme.colors.deepTeal,
    fontWeight: appTheme.typography.fontWeights.bold,
    fontSize: appTheme.typography.fontSizes.small,
    // textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "none",
  }
}));

const ActionButton = styled(Button)(({ variant, color }) => ({
  borderRadius: appTheme.borderRadius.medium,
  textTransform: "none",
  fontWeight: appTheme.typography.fontWeights.semibold,
  padding: `${appTheme.spacing.sm} ${appTheme.spacing.md}`,
  boxShadow: variant === "contained" ? appTheme.shadows.soft : "none",
  transition: appTheme.transitions.luxury,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: variant === "contained" ? appTheme.shadows.medium : appTheme.shadows.soft,
  }
}));

const SearchField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: appTheme.borderRadius.medium,
    backgroundColor: appTheme.colors.surface,
    boxShadow: appTheme.shadows.whisper,
    "& fieldset": {
      borderColor: appTheme.colors.borderPrimary,
    },
    "&:hover fieldset": {
      borderColor: appTheme.colors.primary,
    },
    "&.Mui-focused fieldset": {
      borderColor: appTheme.colors.primary,
      borderWidth: "2px",
    },
  },
});

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: appTheme.borderRadius.rounded,
  background: appTheme.gradients.cardLuxury,
  boxShadow: appTheme.shadows.goldGlow,
  transition: appTheme.transitions.luxury,
  border: `1px solid ${appTheme.colors.borderAccent}`,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: appTheme.shadows.dramatic,
  }
}));

const statusColors = {
  pending: appTheme.colors.warning,
  accepted: appTheme.colors.info,
  shipped: appTheme.colors.primary,
  delivered: appTheme.colors.success,
  cancelled: appTheme.colors.error
};

const paymentStatusColors = {
  paid: appTheme.colors.success,
  pending: appTheme.colors.warning
};

const InvoiceScreen = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [editingPaymentInvoiceId, setEditingPaymentInvoiceId] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    orderStatus: null,
    paymentStatus: null,
    cutoffTimeEnabled: null
  });
  const [showFilters, setShowFilters] = useState(false);
  const [statsExpanded, setStatsExpanded] = useState(true);

  const isMobile = useMediaQuery("(max-width:600px)");

  // useEffect(() => {
  //   // **CORRECTED SAMPLE DATA WITH YOUR ACTUAL IDS**
  //   const sampleInvoicesWithCorrectIds = [
  //     // Invoice 1: Restaurant "first test" - Today's data
  //     {
  //       "id": "INV_TODAY_001",
  //       "address": "test",
  //       "createdAt": new Date().toISOString(), // TODAY's date
  //       "updatedAt": new Date().toISOString(),
  //       "cutoffTimeEnabled": true,
  //       "deliveryCharges": 5.50,
  //       "email": "sreedarpariserla0@gmail.com",
  //       "isBillPaid": false,
  //       "name": "dummy",
  //       "orderStatus": "pending",
  //       "phone": "566766665555",
  //       "restaurantName": "first test",
  //       "userId": "MI9z74I0uTJoDIjvQ5u0", // YOUR ACTUAL RESTAURANT ID
  //       "items": [
  //         {
  //           "id": "HDfitAQ1ByG0E9MJXljK", // demo1
  //           "refId": "HDfitAQ1ByG0E9MJXljK",
  //           "title": "demo1",
  //           "brand": "SOME X BRAND",
  //           "categoryId": "tygXkbrrW7A1tJ20zAlM", // CORRECT CATEGORY ID for "demo 1"
  //           "collection": "inventoryItems",
  //           "itemType": "product",
  //           "quantity": 5,
  //           "units": "KG",
  //           "price": 3.00
  //         },
  //         {
  //           "id": "jYBO0m37XvmOuXcMrXGn", // rice
  //           "refId": "jYBO0m37XvmOuXcMrXGn",
  //           "title": "rice",
  //           "brand": "NA",
  //           "categoryId": "IzJx4xwwUqtWC1ru08XR", // CORRECT CATEGORY ID for "cat1"
  //           "collection": "inventoryItems", 
  //           "itemType": "product",
  //           "quantity": 10,
  //           "units": "KG",
  //           "price": 12.00
  //         }
  //       ],
  //       "subtotal": 135.00,
  //       "tax": 27.00,
  //       "totalPrice": 162.00
  //     },

  //     // Invoice 2: Today's data - Different time
  //     {
  //       "id": "INV_TODAY_002",
  //       "address": "test",
  //       "createdAt": new Date(new Date().getTime() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
  //       "updatedAt": new Date().toISOString(),
  //       "cutoffTimeEnabled": false,
  //       "deliveryCharges": 0,
  //       "email": "sreedarpariserla0@gmail.com",
  //       "isBillPaid": true,
  //       "name": "dummy",
  //       "orderStatus": "delivered",
  //       "phone": "566766665555",
  //       "restaurantName": "first test",
  //       "userId": "MI9z74I0uTJoDIjvQ5u0", // YOUR ACTUAL RESTAURANT ID
  //       "items": [
  //         {
  //           "id": "HDfitAQ1ByG0E9MJXljK", // demo1 again
  //           "refId": "HDfitAQ1ByG0E9MJXljK",
  //           "title": "demo1",
  //           "brand": "SOME X BRAND",
  //           "categoryId": "tygXkbrrW7A1tJ20zAlM", // CORRECT CATEGORY ID
  //           "collection": "inventoryItems",
  //           "itemType": "product",
  //           "quantity": 3,
  //           "units": "KG",
  //           "price": 3.00
  //         },
  //         {
  //           "id": "oEimnZAODR8tDq5AUHAr", // zemo
  //           "refId": "oEimnZAODR8tDq5AUHAr",
  //           "title": "zemo",
  //           "brand": "NA",
  //           "categoryId": "tygXkbrrW7A1tJ20zAlM", // CORRECT CATEGORY ID for "demo 1"
  //           "collection": "inventoryItems",
  //           "itemType": "product",
  //           "quantity": 2,
  //           "units": "Box",
  //           "price": 7.00
  //         }
  //       ],
  //       "subtotal": 23.00,
  //       "tax": 4.50,
  //       "totalPrice": 27.50
  //     },

  //     // Invoice 3: Historical data for performance chart
  //     {
  //       "id": "INV_HIST_001",
  //       "address": "test",
  //       "createdAt": "2025-09-01T10:30:00.000Z",
  //       "updatedAt": "2025-09-01T10:30:00.000Z",
  //       "cutoffTimeEnabled": true,
  //       "deliveryCharges": 3.00,
  //       "email": "sreedarpariserla0@gmail.com",
  //       "isBillPaid": false,
  //       "name": "dummy",
  //       "orderStatus": "shipped",
  //       "phone": "566766665555",
  //       "restaurantName": "first test",
  //       "userId": "MI9z74I0uTJoDIjvQ5u0", // YOUR ACTUAL RESTAURANT ID
  //       "items": [
  //         {
  //           "id": "jYBO0m37XvmOuXcMrXGn", // rice
  //           "refId": "jYBO0m37XvmOuXcMrXGn", 
  //           "title": "rice",
  //           "brand": "NA",
  //           "categoryId": "IzJx4xwwUqtWC1ru08XR", // CORRECT CATEGORY ID
  //           "collection": "inventoryItems",
  //           "itemType": "product",
  //           "quantity": 15,
  //           "units": "KG",
  //           "price": 12.00
  //         },
  //         {
  //           "id": "oEimnZAODR8tDq5AUHAr", // zemo
  //           "refId": "oEimnZAODR8tDq5AUHAr",
  //           "title": "zemo", 
  //           "brand": "NA",
  //           "categoryId": "tygXkbrrW7A1tJ20zAlM", // CORRECT CATEGORY ID
  //           "collection": "inventoryItems",
  //           "itemType": "product",
  //           "quantity": 5,
  //           "units": "Box",
  //           "price": 7.00
  //         }
  //       ],
  //       "subtotal": 215.00,
  //       "tax": 43.00,
  //       "totalPrice": 258.00
  //     }
  //   ];

  //   // **FIXED UPLOAD FUNCTION**
  //   const uploadCorrectedData = async () => {
  //     console.log("🚀 Starting corrected data upload...");

  //     // Upload all sample invoices with correct IDs
  //     for (const invoice of sampleInvoicesWithCorrectIds) {
  //       try {
  //         await setDoc(doc(db, "invoices", invoice.id), invoice);
  //         console.log(`✅ Uploaded invoice: ${invoice.id} - ${invoice.restaurantName}`);
  //       } catch (error) {
  //         console.error(`❌ Failed to upload invoice ${invoice.id}:`, error);
  //       }
  //     }

  //     console.log("🎉 Corrected data upload completed!");
  //   };

  //   // Upload the corrected data
  //   uploadCorrectedData();
  // }, []); // Run once on mount




  // **SAFE HELPERS**
  const safeToFixed = (value, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '0.00';
    return Number(value).toFixed(Number);
  };

  const safeNumber = (value) => {
    if (value === undefined || value === null || isNaN(value)) return 0;
    return Number(value);
  };

  const safeAlpha = (color, opacity) => {
    if (!color) return alpha('#000000', opacity);
    try {
      return alpha(color, opacity);
    } catch (e) {
      console.warn('Invalid color:', color);
      return alpha('#000000', opacity);
    }
  };

  // **SEARCH OPTIMIZATION**
  const debounceTimerRef = useRef(null);

  const debouncedSetSearchQuery = useRef(
    debounce((value) => {
      setSearchQuery(value);
    }, 300)
  ).current;

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSetSearchQuery(value);
  }, [debouncedSetSearchQuery]);

  useEffect(() => {
    return () => {
      if (debouncedSetSearchQuery.cancel) {
        debouncedSetSearchQuery.cancel();
      }
    };
  }, [debouncedSetSearchQuery]);
  // **CALCULATE TOTALS WITH VAT**
  const calculateTotals = useMemo(() => {
    const totals = filteredInvoices.reduce((acc, invoice) => {
      const subtotal = safeNumber(invoice.subtotal);
      const tax = safeNumber(invoice.tax);
      const totalPrice = safeNumber(invoice.totalPrice);

      return {
        totalOrders: acc.totalOrders + 1,
        totalSubtotal: acc.totalSubtotal + subtotal,
        totalVAT: acc.totalVAT + tax,
        totalRevenue: acc.totalRevenue + totalPrice,
        averageOrderValue: 0, // Will calculate after
      };
    }, {
      totalOrders: 0,
      totalSubtotal: 0,
      totalVAT: 0,
      totalRevenue: 0,
      averageOrderValue: 0,
    });

    totals.averageOrderValue = totals.totalOrders > 0 ? totals.totalRevenue / totals.totalOrders : 0;
    return totals;
  }, [filteredInvoices]);

  useEffect(() => {
    const q = query(collection(db, "invoices"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const invoiceData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setInvoices(invoiceData);
      setFilteredInvoices(invoiceData);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // **FILTERING LOGIC**
  const applyFilters = useCallback(() => {
    let result = [...invoices];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(invoice =>
        (invoice.restaurantName?.toLowerCase() || '').includes(query) ||
        (invoice.name?.toLowerCase() || '').includes(query) ||
        (invoice.id?.toLowerCase() || '').includes(query)
      );
    }

    if (filters.orderStatus) {
      result = result.filter(invoice => invoice.orderStatus === filters.orderStatus);
    }

    if (filters.paymentStatus) {
      const isPaid = filters.paymentStatus === 'paid';
      result = result.filter(invoice => invoice.isBillPaid === isPaid);
    }

    if (filters.cutoffTimeEnabled !== null) {
      result = result.filter(invoice => invoice.cutoffTimeEnabled === filters.cutoffTimeEnabled);
    }

    setFilteredInvoices(result);
    setPage(0);
  }, [invoices, searchQuery, filters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // **SORTING**
  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sorted = [...filteredInvoices].sort((a, b) => {
      if (column === "totalPrice" || column === "tax" || column === "subtotal") {
        return direction === "asc"
          ? safeNumber(a[column]) - safeNumber(b[column])
          : safeNumber(b[column]) - safeNumber(a[column]);
      }

      if (column === "createdAt") {
        return direction === "asc"
          ? new Date(a[column]) - new Date(b[column])
          : new Date(b[column]) - new Date(a[column]);
      }

      if (a[column] < b[column]) return direction === "asc" ? -1 : 1;
      if (a[column] > b[column]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredInvoices(sorted);
    setSortConfig({ key: column, direction });
  };

  // **OTHER HANDLERS** (keeping existing logic for status updates, delete, etc.)
  const handleOrderStatusChange = async (invoiceId, newStatus) => {
    try {
      const invoiceRef = doc(db, "invoices", invoiceId);
      await updateDoc(invoiceRef, { orderStatus: newStatus });
      setEditingInvoiceId(null);
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  const handlePaymentStatusChange = async (invoiceId, isPaid) => {
    try {
      const invoiceRef = doc(db, "invoices", invoiceId);
      await updateDoc(invoiceRef, { isBillPaid: isPaid });
      setEditingPaymentInvoiceId(null);
    } catch (error) {
      console.error("Error updating payment status:", error);
    }
  };

  // **DELETE HANDLER** (keeping existing logic)
  const handleDelete = async () => {
    const updateSoldQuantity = async (item) => {
      const q = query(
        collection(db, "inventoryItems"),
        where("title", "==", item.title),
        where("brand", "==", item.brand)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        const inventoryItem = querySnapshot.docs[0].data();
        await updateDoc(docRef, {
          soldQuantity: Math.max(0, inventoryItem.soldQuantity - item.quantity),
        });
      }
    };

    if (deleteType === "single" && invoiceToDelete) {
      const invoiceDoc = await getDoc(doc(db, "invoices", invoiceToDelete));
      if (invoiceDoc.exists()) {
        const invoice = invoiceDoc.data();
        for (const item of invoice.items) {
          await updateSoldQuantity(item);
        }
        await deleteDoc(doc(db, "invoices", invoiceToDelete));
        setFilteredInvoices((prev) =>
          prev.filter((invoice) => invoice.id !== invoiceToDelete)
        );
      }
    } else if (deleteType === "multiple") {
      for (const id of selectedInvoices) {
        const invoiceDoc = await getDoc(doc(db, "invoices", id));
        if (invoiceDoc.exists()) {
          const invoice = invoiceDoc.data();
          for (const item of invoice.items) {
            await updateSoldQuantity(item);
          }
          await deleteDoc(doc(db, "invoices", id));
        }
      }
      setInvoices((prev) =>
        prev.filter((invoice) => !selectedInvoices.includes(invoice.id))
      );
      setFilteredInvoices((prev) =>
        prev.filter((invoice) => !selectedInvoices.includes(invoice.id))
      );
    }

    setDeleteDialogOpen(false);
    setSelectedInvoices([]);
    setInvoiceToDelete(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleSelectInvoice = (id) => {
    setSelectedInvoices((prev) =>
      prev.includes(id)
        ? prev.filter((invoiceId) => invoiceId !== id)
        : [...prev, id]
    );
  };

  const isAllSelected = filteredInvoices.length > 0 && selectedInvoices.length === filteredInvoices.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(filteredInvoices.map((invoice) => invoice.id));
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value === "all" ? null : value
    }));
  };

  const clearFilters = () => {
    setFilters({
      orderStatus: null,
      paymentStatus: null,
      cutoffTimeEnabled: null
    });
    setInputValue("");
    setSearchQuery("");
  };

  const getCurrentPageData = () => {
    return filteredInvoices.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  };

  const hasFilters = filters.orderStatus || filters.paymentStatus || filters.cutoffTimeEnabled !== null;

  return (
    <Box sx={{
      padding: appTheme.spacing.lg,
      backgroundColor: appTheme.colors.surfaceLuxury,
      minHeight: "100vh"
    }}>
      {/* **HEADER WITH TOGGLE** */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: appTheme.spacing.xl
      }}>
        <Box>
          <Typography variant="h3" sx={{
            fontWeight: appTheme.typography.fontWeights.black,
            color: appTheme.colors.primary,
            marginBottom: appTheme.spacing.xs,
            background: appTheme.gradients.hero,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>
            🧾 Order Management
          </Typography>
          <Typography variant="h6" sx={{
            color: appTheme.colors.textSecondary,
            fontWeight: appTheme.typography.fontWeights.regular
          }}>
            Track orders and manage billing with VAT calculations
          </Typography>
        </Box>

        <ActionButton
          variant="outlined"
          onClick={() => setStatsExpanded(!statsExpanded)}
          startIcon={!statsExpanded ? <ArrowDownIcon /> : <ArrowUpIcon />}
          sx={{
            borderColor: appTheme.colors.accent,
            color: appTheme.colors.accent,
            backgroundColor: appTheme.colors.surface,
            "&:hover": {
              backgroundColor: alpha(appTheme.colors.accent, 0.1),
            }
          }}
        >
          {statsExpanded ? "Hide Stats" : "Show Stats"}
        </ActionButton>
      </Box>

      {/* **COLLAPSIBLE STATISTICS** */}
      <Collapse in={statsExpanded} timeout={500}>
        <Grid container spacing={3} sx={{ marginBottom: appTheme.spacing.xl }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, padding: appTheme.spacing.lg }}>
                <Box sx={{
                  backgroundColor: appTheme.colors.primary,
                  borderRadius: appTheme.borderRadius.rounded,
                  padding: appTheme.spacing.md,
                  color: appTheme.colors.champagne
                }}>
                  <ReceiptIcon sx={{ fontSize: "2rem", mr: 1 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h4" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.primary,
                    fontSize: { xs: "1.75rem", sm: "2.125rem" },
                    wordWrap: "break-word"
                  }}>
                    {calculateTotals.totalOrders}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: appTheme.colors.textSecondary,
                    fontWeight: appTheme.typography.fontWeights.medium
                  }}>
                    Total Orders
                  </Typography>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard sx={{ background: appTheme.gradients.cardPremium }}>
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, padding: appTheme.spacing.lg }}>
                <Box sx={{
                  backgroundColor: appTheme.colors.secondary,
                  borderRadius: appTheme.borderRadius.rounded,
                  padding: appTheme.spacing.md,
                  color: appTheme.colors.champagne
                }}>
                  <TrendingUp sx={{ fontSize: "2rem" }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h4" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.secondary,
                    fontSize: { xs: "1.75rem", sm: "2.125rem" },
                    wordWrap: "break-word"
                  }}>
                    £{safeToFixed(calculateTotals.totalRevenue)}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: appTheme.colors.textSecondary,
                    fontWeight: appTheme.typography.fontWeights.medium
                  }}>
                    Total Revenue
                  </Typography>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard sx={{
              background: `linear-gradient(135deg, ${appTheme.colors.accent} 0%, ${appTheme.colors.goldenHour} 100%)`
            }}>
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, padding: appTheme.spacing.lg }}>
                <Box sx={{
                  backgroundColor: appTheme.colors.accent,
                  borderRadius: appTheme.borderRadius.rounded,
                  padding: appTheme.spacing.md,
                  color: appTheme.colors.charcoal
                }}>
                  <CreditCard sx={{ fontSize: "2rem" }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h4" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.accent,
                    fontSize: { xs: "1.75rem", sm: "2.125rem" },
                    wordWrap: "break-word"
                  }}>
                    £{safeToFixed(calculateTotals.totalVAT)}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: appTheme.colors.textSecondary,
                    fontWeight: appTheme.typography.fontWeights.medium
                  }}>
                    Total VAT Collected
                  </Typography>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard sx={{
              background: `linear-gradient(135deg, ${appTheme.colors.success} 0%, ${appTheme.colors.jadeFog} 100%)`
            }}>
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, padding: appTheme.spacing.lg }}>
                <Box sx={{
                  backgroundColor: appTheme.colors.success,
                  borderRadius: appTheme.borderRadius.rounded,
                  padding: appTheme.spacing.md,
                  color: appTheme.colors.surface
                }}>
                  <ShoppingCart sx={{ fontSize: "2rem" }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h4" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.success,
                    fontSize: { xs: "1.75rem", sm: "2.125rem" },
                    wordWrap: "break-word"
                  }}>
                    £{safeToFixed(calculateTotals.averageOrderValue)}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: appTheme.colors.textSecondary,
                    fontWeight: appTheme.typography.fontWeights.medium
                  }}>
                    Avg Order Val
                  </Typography>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>
      </Collapse>

      {/* **CONTROLS SECTION** */}
      <StyledCard sx={{ marginBottom: appTheme.spacing.lg, padding: appTheme.spacing.lg }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <SearchField
              key="invoice-search"
              fullWidth
              label="Search by restaurant, customer, or order ID"
              variant="outlined"
              value={inputValue}
              onChange={handleSearchChange}
              placeholder="Type to search orders..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: appTheme.colors.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: { xs: "center", md: "flex-end" } }}>
              <ActionButton
                variant="outlined"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<Filter />}
                sx={{
                  borderColor: appTheme.colors.primary,
                  color: showFilters ? appTheme.colors.surface : appTheme.colors.primary,
                  backgroundColor: showFilters ? appTheme.colors.primary : 'transparent',
                  '&:hover': {
                    backgroundColor: showFilters
                      ? safeAlpha(appTheme.colors.primary, 0.9)
                      : safeAlpha(appTheme.colors.primary, 0.1),
                  }
                }}
              >
                Filters
              </ActionButton>

              <ActionButton
                variant="contained"
                disabled={selectedInvoices.length === 0}
                onClick={() => {
                  setDeleteDialogOpen(true);
                  setDeleteType("multiple");
                }}
                startIcon={<Trash2 />}
                sx={{
                  backgroundColor: selectedInvoices.length === 0
                    ? appTheme.colors.rosePetal
                    : appTheme.colors.error,
                  color: appTheme.colors.surface,
                  '&:hover': {
                    backgroundColor: selectedInvoices.length === 0
                      ? appTheme.colors.rosePetal
                      : appTheme.colors.warmCopper,
                  }
                }}
              >
                Delete ({selectedInvoices.length})
              </ActionButton>
            </Box>
          </Grid>
        </Grid>

        {/* **FILTERS** */}
        <Collapse in={showFilters} timeout={300}>
          <Divider sx={{ margin: `${appTheme.spacing.md} 0` }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Order Status</InputLabel>
                <Select
                  value={filters.orderStatus || "all"}
                  onChange={(e) => handleFilterChange("orderStatus", e.target.value)}
                  label="Order Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="shipped">Shipped</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={filters.paymentStatus || "all"}
                  onChange={(e) => handleFilterChange("paymentStatus", e.target.value)}
                  label="Payment Status"
                >
                  <MenuItem value="all">All Payments</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {hasFilters && (
                  <ActionButton
                    variant="text"
                    onClick={clearFilters}
                    startIcon={<X />}
                    sx={{ color: appTheme.colors.error }}
                  >
                    Clear Filters
                  </ActionButton>
                )}
              </Box>
            </Grid>
          </Grid>
        </Collapse>

        {/* **ACTIVE FILTERS CHIPS** */}
        {hasFilters && (
          <Box sx={{ marginTop: appTheme.spacing.md }}>
            <Typography variant="body2" sx={{ marginBottom: appTheme.spacing.sm, fontWeight: 600 }}>
              Active filters:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {filters.orderStatus && (
                <Chip
                  label={`Status: ${filters.orderStatus}`}
                  onDelete={() => handleFilterChange("orderStatus", null)}
                  sx={{
                    backgroundColor: safeAlpha(statusColors[filters.orderStatus], 0.2),
                    color: appTheme.colors.textPrimary
                  }}
                />
              )}
              {filters.paymentStatus && (
                <Chip
                  label={`Payment: ${filters.paymentStatus}`}
                  onDelete={() => handleFilterChange("paymentStatus", null)}
                  sx={{
                    backgroundColor: safeAlpha(paymentStatusColors[filters.paymentStatus], 0.2),
                    color: appTheme.colors.textPrimary
                  }}
                />
              )}
            </Box>
          </Box>
        )}
      </StyledCard>

      {/* **ENHANCED TABLE WITH VAT COLUMN** */}
      <StyledTableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <CircularProgress size={50} sx={{ color: appTheme.colors.primary }} />
          </Box>
        ) : (
          <Table stickyHeader>
            <StyledTableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedInvoices.length > 0 && !isAllSelected}
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    sx={{
                      color: "#FFFFFF",
                      '&.Mui-checked': { color: "#FFFFFF" },
                      '&.MuiCheckbox-indeterminate': { color: "#FFFFFF" },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === "id"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("id")}
                    sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                  >
                    Order ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Restaurant</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === "subtotal"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("subtotal")}
                    sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                  >
                    Subtotal
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === "tax"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("tax")}
                    sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                  >
                    VAT
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === "totalPrice"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("totalPrice")}
                    sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                  >
                    Total
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === "createdAt"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("createdAt")}
                    sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>Order Status</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </StyledTableHead>

            <TableBody>
              {getCurrentPageData().map((invoice) => (
                <TableRow key={invoice.id} sx={{
                  "&:hover": {
                    backgroundColor: alpha(appTheme.colors.primary, 0.05),
                  }
                }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedInvoices.includes(invoice.id)}
                      onChange={() => toggleSelectInvoice(invoice.id)}
                      sx={{
                        color: appTheme.colors.primary,
                        '&.Mui-checked': { color: appTheme.colors.primary },
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: appTheme.colors.primary }}>
                      #{invoice.id.slice(0, 8)}...
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {invoice.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: appTheme.colors.textSecondary }}>
                        {invoice.email}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {invoice.restaurantName}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Badge
                      badgeContent={invoice.items?.length || 0}
                      sx={{
                        "& .MuiBadge-badge": {
                          backgroundColor: appTheme.colors.accent,
                          color: appTheme.colors.charcoal,
                          fontWeight: 600,
                        }
                      }}
                    >
                      <ShoppingCart sx={{ color: appTheme.colors.textSecondary }} />
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      £{safeToFixed(invoice.subtotal || (invoice.totalPrice - invoice.tax))}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{
                      fontWeight: 500,
                      color: appTheme.colors.accent
                    }}>
                      £{safeToFixed(invoice.tax)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{
                      fontWeight: 700,
                      color: appTheme.colors.success
                    }}>
                      £{safeToFixed(invoice.totalPrice)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ color: appTheme.colors.textSecondary }}>
                      {new Date(invoice.createdAt).toLocaleDateString('en-GB')}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    {editingInvoiceId === invoice.id ? (
                      <Select
                        value={invoice.orderStatus}
                        onChange={(e) => handleOrderStatusChange(invoice.id, e.target.value)}
                        onBlur={() => setEditingInvoiceId(null)}
                        size="small"
                        fullWidth
                      // renderValue={(selected) => selected.charAt(0).toUpperCase() + selected.slice(1)}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="accepted">Accepted</MenuItem>
                        <MenuItem value="shipped">Shipped</MenuItem>
                        <MenuItem value="delivered">Delivered</MenuItem>
                      </Select>
                    ) : (
                      <Chip
                        label={invoice.orderStatus.charAt(0).toUpperCase() + invoice.orderStatus.slice(1)}
                        onClick={() => setEditingInvoiceId(invoice.id)}
                        sx={{
                          backgroundColor: safeAlpha(statusColors[invoice.orderStatus] || appTheme.colors.neutral[400], 0.2),
                          color: statusColors[invoice.orderStatus] || appTheme.colors.textSecondary,
                          fontWeight: 600,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: safeAlpha(statusColors[invoice.orderStatus] || appTheme.colors.neutral[400], 0.3),
                          }
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell>
                    {editingPaymentInvoiceId === invoice.id ? (
                      <Select
                        value={invoice.isBillPaid ? "paid" : "pending"}
                        onChange={(e) => handlePaymentStatusChange(invoice.id, e.target.value === "paid")}
                        onBlur={() => setEditingPaymentInvoiceId(null)}
                        size="small"
                        fullWidth
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                      </Select>
                    ) : (
                      <Chip
                        label={invoice.isBillPaid ? "Paid" : "Pending"}
                        onClick={() => setEditingPaymentInvoiceId(invoice.id)}
                        sx={{
                          backgroundColor: safeAlpha(
                            invoice.isBillPaid ? appTheme.colors.success : appTheme.colors.warning,
                            0.2
                          ),
                          color: invoice.isBillPaid ? appTheme.colors.success : appTheme.colors.warning,
                          fontWeight: 600,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: safeAlpha(
                              invoice.isBillPaid ? appTheme.colors.success : appTheme.colors.warning,
                              0.3
                            ),
                          }
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Invoice">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setIsModalOpen(true);
                          }}
                          sx={{
                            color: appTheme.colors.primary,
                            "&:hover": {
                              backgroundColor: alpha(appTheme.colors.primary, 0.1),
                            }
                          }}
                        >
                          <Eye size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Invoice">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDeleteDialogOpen(true);
                            setDeleteType("single");
                            setInvoiceToDelete(invoice.id);
                          }}
                          sx={{
                            color: appTheme.colors.error,
                            "&:hover": {
                              backgroundColor: alpha(appTheme.colors.error, 0.1),
                            }
                          }}
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* **PAGINATION** */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredInvoices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            backgroundColor: appTheme.colors.surfaceElevated,
            "& .MuiTablePagination-toolbar": {
              backgroundColor: appTheme.colors.surfaceElevated,
            }
          }}
        />
      </StyledTableContainer>

      {/* **DELETE CONFIRMATION DIALOG** */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: appTheme.borderRadius.rounded,
            padding: appTheme.spacing.sm,
          }
        }}
      >
        <DialogTitle sx={{
          color: appTheme.colors.error,
          fontWeight: appTheme.typography.fontWeights.bold,
          display: "flex",
          alignItems: "center",
          gap: 1
        }}>
          <Trash2 /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            {deleteType === "multiple"
              ? `Are you sure you want to delete ${selectedInvoices.length} selected orders? This action cannot be undone.`
              : "Are you sure you want to delete this order? This action cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <ActionButton onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </ActionButton>
          <ActionButton
            onClick={handleDelete}
            variant="contained"
            sx={{ backgroundColor: appTheme.colors.error }}
          >
            Delete
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* **INVOICE MODAL** */}
      {selectedInvoice && (
        <InvoiceModal
          invoice={selectedInvoice}
          handleClose={() => {
            setSelectedInvoice(null);
            setIsModalOpen(false);
          }}
          isModalOpen={isModalOpen}
        />
      )}
    </Box>
  );
};

export default InvoiceScreen;
